import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { BarChart3, CheckCircle2, Lock, Users, Vote, RefreshCw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { getAnonId } from "@/lib/anonId";

const formatDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString("pl-PL", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return null; }
};

/**
 * Compact PollWidget designed to sit side-by-side with Newsletter on a dark background.
 * Use prop `compact` to render the inner card only (without section wrapper / max-width).
 */
export default function PollWidget({ compact = false }) {
    const [poll, setPoll] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [showResults, setShowResults] = useState(false);

    const anonId = typeof window !== "undefined" ? getAnonId() : "";

    const load = async () => {
        try {
            const r = await api.get("/polls/active", { params: { anonymous_id: anonId } });
            setPoll(r.data || null);
            if (r.data?.my_vote?.length) {
                setSelected(r.data.my_vote);
                setShowResults(true);
            }
        } catch { /* ignore */ } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    if (loading || !poll) return null;

    const closed = poll.is_closed;
    const hasVoted = (poll.my_vote || []).length > 0;
    const showBars = showResults || hasVoted || closed;

    const toggleOption = (id) => {
        if (poll.multi_select) {
            setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
        } else {
            setSelected([id]);
        }
    };

    const submit = async () => {
        if (selected.length === 0) {
            toast.error("Wybierz odpowiedź przed wysłaniem.");
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.post(`/polls/${poll.id}/vote`, {
                option_ids: selected,
                anonymous_id: anonId,
                website: "",
            });
            if (r.data?.already_voted) {
                toast.info("Już oddałeś głos w tej sondzie.");
            } else if (r.data?.ok) {
                toast.success("Dziękujemy za głos!");
            }
            await load();
            setShowResults(true);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setSubmitting(false); }
    };

    const card = (
        <div className="h-full flex flex-col" data-testid="poll-widget">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#D4A537]/20 mb-5">
                <Vote className="text-[#D4A537]" size={24} />
            </div>
            <p className="text-[#D4A537] text-[10px] uppercase tracking-[0.35em] font-semibold mb-2">Sonda mieszkańców</p>
            <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-2 break-words" data-testid="poll-question">{poll.question}</h3>
            {poll.description && <p className="text-white/70 text-sm leading-relaxed break-words mb-4">{poll.description}</p>}

            {closed && (
                <div className="flex items-center gap-2 text-amber-300 bg-amber-900/30 border border-amber-700/40 rounded-xl px-3 py-2 mb-3 text-xs" data-testid="poll-closed-banner">
                    <Lock size={12} /> Sonda zamknięta — pokazujemy ostateczne wyniki.
                </div>
            )}
            {hasVoted && !closed && (
                <div className="flex items-center gap-2 text-emerald-300 bg-emerald-900/30 border border-emerald-700/40 rounded-xl px-3 py-2 mb-3 text-xs" data-testid="poll-already-voted">
                    <CheckCircle2 size={12} /> Dziękujemy za Twój głos!
                </div>
            )}

            <div className="space-y-2 flex-1" data-testid="poll-options">
                {poll.options.map((opt) => {
                    const isMine = (poll.my_vote || []).includes(opt.id);
                    const isSelected = selected.includes(opt.id);
                    return (
                        <div key={opt.id} className="relative">
                            {showBars ? (
                                <div
                                    data-testid={`poll-result-${opt.id}`}
                                    className={`relative border rounded-xl p-3 overflow-hidden ${isMine ? "border-[#D4A537]/70 bg-[#D4A537]/10" : "border-white/15 bg-white/5"}`}
                                >
                                    <AnimatePresence>
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${opt.percent}%` }}
                                            transition={{ duration: 0.9, ease: "easeOut" }}
                                            className={`absolute inset-y-0 left-0 ${isMine ? "bg-gradient-to-r from-[#D4A537]/40 to-[#D4A537]/15" : "bg-gradient-to-r from-white/15 to-white/5"}`}
                                        />
                                    </AnimatePresence>
                                    <div className="relative flex items-center justify-between gap-3">
                                        <span className="font-medium text-white flex items-center gap-2 break-words text-sm">
                                            {isMine && <CheckCircle2 size={14} className="text-[#D4A537] shrink-0" />}
                                            {opt.label}
                                        </span>
                                        <span className="text-xs font-semibold text-white shrink-0 tabular-nums">
                                            {opt.percent}% <span className="text-white/50 font-normal">({opt.votes})</span>
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <label
                                    data-testid={`poll-option-${opt.id}`}
                                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${isSelected ? "border-[#D4A537] bg-[#D4A537]/10" : "border-white/15 bg-white/5 hover:border-[#D4A537]/60"}`}
                                >
                                    <input
                                        type={poll.multi_select ? "checkbox" : "radio"}
                                        name={`poll-${poll.id}`}
                                        checked={isSelected}
                                        onChange={() => toggleOption(opt.id)}
                                        className="w-4 h-4 accent-[#D4A537]"
                                    />
                                    <span className="text-white font-medium flex-1 break-words text-sm">{opt.label}</span>
                                </label>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-white/10">
                <div className="flex items-center gap-2 text-[11px] text-white/60">
                    <Users size={11} />
                    <span data-testid="poll-total-votes">{poll.total_votes} głosów</span>
                    {poll.multi_select && <span className="text-[9px] uppercase tracking-wider bg-blue-500/20 text-blue-200 px-1.5 py-0.5 rounded-full">multi</span>}
                    {poll.ends_at && !closed && <span className="text-[10px]">do {formatDate(poll.ends_at)}</span>}
                </div>
                <div className="flex items-center gap-2 ml-auto">
                    {!hasVoted && !closed && !showResults && (
                        <>
                            <button
                                onClick={submit}
                                disabled={submitting || selected.length === 0}
                                data-testid="poll-submit"
                                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#D4A537] hover:bg-[#E5B848] text-[#092E22] text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                <Vote size={14} /> {submitting ? "..." : "Oddaj głos"}
                            </button>
                            <button
                                onClick={() => setShowResults(true)}
                                data-testid="poll-show-results"
                                className="text-[10px] text-white/70 hover:text-[#D4A537] inline-flex items-center gap-1 underline decoration-dotted"
                            >
                                <BarChart3 size={11} /> Wyniki
                            </button>
                        </>
                    )}
                    {showResults && !hasVoted && !closed && (
                        <button
                            onClick={() => setShowResults(false)}
                            data-testid="poll-back-to-vote"
                            className="text-[10px] text-white/70 hover:text-[#D4A537] inline-flex items-center gap-1 underline decoration-dotted"
                        >
                            <RefreshCw size={11} /> Powrót do głosowania
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    if (compact) return card;

    return (
        <section id="sonda" className="py-16">
            <div className="max-w-3xl mx-auto px-6 lg:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="rounded-[2rem] bg-gradient-to-br from-[#0D4F3C] to-[#092E22] p-8 md:p-10 text-white"
                >
                    {card}
                </motion.div>
            </div>
        </section>
    );
}
